/**
 * Menu Filter Card
 *
 * Allows restaurant owners to select which menu sections are available
 * for AI Host ordering. Excluded sections won't be offered to customers.
 *
 * Features:
 * - Chip-based UI for easy toggling
 * - Green = included, Gray = excluded
 * - Auto-save on change
 *
 * Author: Dine AI
 * Created: November 25, 2025
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Filter, Check, X, Loader, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';

interface MenuFilterCardProps {
  className?: string;
}

const MenuFilterCard: React.FC<MenuFilterCardProps> = ({ className = '' }) => {
  const [allSections, setAllSections] = useState<string[]>([]);
  const [excludedSections, setExcludedSections] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingSection, setSavingSection] = useState<string | null>(null);

  /**
   * Fetch menu sections from backend
   */
  const fetchSections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/virtual-host/settings/menu-sections');
      const data = response.data;

      setAllSections(data.all_sections || []);
      setExcludedSections(data.excluded_sections || []);
    } catch (error) {
      console.error('Error fetching menu sections:', error);
      toast.error('Failed to load menu sections');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle a section's inclusion/exclusion
   */
  const toggleSection = async (section: string) => {
    const isCurrentlyExcluded = excludedSections.includes(section);
    const newExcludedSections = isCurrentlyExcluded
      ? excludedSections.filter(s => s !== section)
      : [...excludedSections, section];

    // Optimistic update
    setExcludedSections(newExcludedSections);
    setSavingSection(section);
    setSaving(true);

    try {
      await apiClient.put('/virtual-host/settings', {
        excluded_sections: newExcludedSections
      });

      const action = isCurrentlyExcluded ? 'included' : 'excluded';
      toast.success(`${section} ${action} from AI Host menu`);
    } catch (error) {
      console.error('Error updating excluded sections:', error);
      toast.error('Failed to update menu filter');
      // Revert on error
      setExcludedSections(excludedSections);
    } finally {
      setSaving(false);
      setSavingSection(null);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  if (loading) {
    return (
      <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Menu Filter</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader className="h-6 w-6 text-gray-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (allSections.length === 0) {
    return (
      <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Menu Filter</h3>
        </div>
        <p className="text-gray-500 text-sm">
          No menu sections found. Upload a menu to see sections here.
        </p>
      </div>
    );
  }

  const includedCount = allSections.length - excludedSections.length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl border border-gray-200 shadow-sm p-6 group ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
          <h3 className="text-lg font-semibold text-gray-900">Menu Filter</h3>
        </div>
        <button
          onClick={fetchSections}
          disabled={loading}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-all border border-gray-200"
          title="Refresh sections"
        >
          <RefreshCw className={`h-4 w-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary */}
      <p className="text-gray-500 text-sm mb-4">
        {includedCount} of {allSections.length} sections available for AI Host
      </p>

      {/* Section Chips */}
      <div className="flex flex-wrap gap-2">
        {allSections.map((section) => {
          const isExcluded = excludedSections.includes(section);
          const isSaving = savingSection === section;

          return (
            <button
              key={section}
              onClick={() => toggleSection(section)}
              disabled={saving}
              className={`
                px-3 py-1.5 rounded-full text-sm font-medium transition-all
                flex items-center gap-1.5
                ${isExcluded
                  ? 'bg-gray-100 text-gray-500 border border-gray-200 hover:bg-gray-200'
                  : 'bg-green-100 text-green-700 border border-green-200 hover:bg-green-200'
                }
                ${saving && !isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {isSaving ? (
                <Loader className="h-3 w-3 animate-spin" />
              ) : isExcluded ? (
                <X className="h-3 w-3" />
              ) : (
                <Check className="h-3 w-3" />
              )}
              {section}
            </button>
          );
        })}
      </div>

      {/* Help Text */}
      <p className="text-gray-400 text-xs mt-4">
        Click a section to include/exclude it from AI Host ordering.
        Gray sections are excluded and won't be offered to customers.
      </p>
    </motion.div>
  );
};

export default MenuFilterCard;
